import { useCallback, useMemo } from 'react';
import { PermissionGroupConfig, getCustomGroup } from '../config/permissionGroups';
import { RolePermission } from '../models/Roles';
import {
  getViewPermission,
  hasOtherPermissionsFromGroup,
  getPermissionPrefix,
  getViewPermissionBaseGroup,
} from '../utils/permissionUtils';

interface UsePermissionSelectionProps {
  allPermissions: RolePermission[];
  selectedPermissions: string[];
  setSelectedPermissions: React.Dispatch<React.SetStateAction<string[]>>;
  customGroupMap: PermissionGroupConfig;
}

/**
 * Hook para manejar la selección/deselección de permisos con lógica de .view automático
 * Esta lógica es específica para tenant_3pl
 */
export const usePermissionSelection = ({
  allPermissions,
  selectedPermissions,
  setSelectedPermissions,
  customGroupMap,
}: UsePermissionSelectionProps) => {
  /**
   * Maneja el cambio de selección de un permiso individual
   * - Si se selecciona un permiso no-.view, automáticamente selecciona su .view
   * - Si se deselecciona un .view, deselecciona todos los permisos de ese grupo
   * - Si se deselecciona un permiso no-.view, deselecciona su .view solo si no hay otros permisos del grupo
   */
  const handlePermissionChange = useCallback(
    (slug: string) => {
      setSelectedPermissions((prev) => {
        const isCurrentlySelected = prev.includes(slug);
        const isViewPermission = slug.endsWith('.view');

        if (isCurrentlySelected) {
          // Si se deselecciona un permiso .view, deseleccionar TODOS los permisos de ese grupo
          if (isViewPermission) {
            const baseGroup = getViewPermissionBaseGroup(slug);
            // Deseleccionar todos los permisos que empiecen con ese grupo
            return prev.filter((pslug) => !pslug.startsWith(baseGroup + '.'));
          }

          // Si se deselecciona un permiso no-.view, también deseleccionar el .view si existe
          let newSelection = prev.filter((pslug) => pslug !== slug);

          const viewSlug = getViewPermission(slug, allPermissions);
          if (viewSlug && prev.includes(viewSlug)) {
            // Verificar si hay otros permisos del grupo seleccionados
            const baseGroup = getPermissionPrefix(slug);
            if (!hasOtherPermissionsFromGroup(baseGroup, newSelection, allPermissions)) {
              // Si no hay otros permisos, deseleccionar también el .view
              newSelection = newSelection.filter((pslug) => pslug !== viewSlug);
            }
          }

          return newSelection;
        } else {
          // Al seleccionar un permiso, automáticamente seleccionar el .view correspondiente
          const newSelection = [...prev, slug];

          if (!isViewPermission) {
            const viewSlug = getViewPermission(slug, allPermissions);
            if (viewSlug && !prev.includes(viewSlug)) {
              newSelection.push(viewSlug);
            }
          }

          return newSelection;
        }
      });
    },
    [allPermissions, setSelectedPermissions]
  );

  /**
   * Maneja la selección/deselección de todos los permisos filtrados
   */
  const handleSelectAll = useCallback(
    (filteredPermissions: RolePermission[]) => {
      const nonViewPermissions = filteredPermissions.filter((p) => !p.slug.endsWith('.view'));
      const allSelected =
        nonViewPermissions.length > 0 &&
        nonViewPermissions.every((perm) => selectedPermissions.includes(perm.slug));

      if (allSelected) {
        // Deseleccionar todos los permisos filtrados
        setSelectedPermissions((prev) =>
          prev.filter((slug) => !filteredPermissions.some((p) => p.slug === slug))
        );
      } else {
        // Seleccionar todos los permisos filtrados y sus .view correspondientes
        setSelectedPermissions((prev) => {
          const newSlugs: string[] = [];
          const viewSlugsToAdd = new Set<string>();

          // Agregar todos los permisos que no estén seleccionados
          filteredPermissions.forEach((perm) => {
            if (!prev.includes(perm.slug)) {
              // Si es un .view, agregarlo directamente
              if (perm.slug.endsWith('.view')) {
                newSlugs.push(perm.slug);
              } else {
                // Si no es .view, agregarlo y también su .view correspondiente
                newSlugs.push(perm.slug);
                const viewSlug = getViewPermission(perm.slug, allPermissions);
                if (
                  viewSlug &&
                  !prev.includes(viewSlug) &&
                  !filteredPermissions.some((p) => p.slug === viewSlug)
                ) {
                  viewSlugsToAdd.add(viewSlug);
                }
              }
            }
          });

          return [...prev, ...newSlugs, ...Array.from(viewSlugsToAdd)];
        });
      }
    },
    [allPermissions, selectedPermissions, setSelectedPermissions]
  );

  /**
   * Maneja la selección/deselección de todos los permisos de un grupo
   */
  const handleGroupSelectAll = useCallback(
    (groupPermissions: RolePermission[]) => {
      if (!allPermissions || groupPermissions.length === 0) return;

      // Verificar si es un grupo personalizado
      const groupName = groupPermissions[0]?.slug;
      const customGroup = getCustomGroup(groupName || '', customGroupMap);

      let allGroupPermissions: RolePermission[] = [];
      let visibleGroupSlugs: string[] = [];

      if (customGroup) {
        // Grupo personalizado: obtener todos los permisos de los prefijos incluidos
        const prefixes = customGroupMap[customGroup];
        allGroupPermissions = allPermissions.filter((p) => {
          const prefix = p.slug.split('.')[0];
          return prefixes.includes(prefix);
        });
        visibleGroupSlugs = groupPermissions.map((p) => p.slug);
      } else {
        // Grupo normal: obtener por prefijo
        const groupKey = groupName?.split('.')[0] || '';
        allGroupPermissions = allPermissions.filter((p) => p.slug.startsWith(groupKey + '.'));
        visibleGroupSlugs = groupPermissions.map((p) => p.slug);
      }

      const groupSlugs = allGroupPermissions.map((p) => p.slug);

      // Verificar si todos los permisos del grupo están seleccionados (incluyendo .view)
      const allSelected =
        visibleGroupSlugs.length > 0 &&
        visibleGroupSlugs.every((slug) => selectedPermissions.includes(slug));

      if (allSelected) {
        // Deseleccionar todos los permisos del grupo
        setSelectedPermissions((prev) => prev.filter((slug) => !groupSlugs.includes(slug)));
      } else {
        // Seleccionar todos los permisos del grupo (incluyendo .view automáticamente)
        setSelectedPermissions((prev) => {
          const newSlugs: string[] = [];
          const viewSlugsToAdd = new Set<string>();

          // Para cada prefijo del grupo, agregar su .view correspondiente solo si no está visible
          if (customGroup) {
            const prefixes = customGroupMap[customGroup];
            prefixes.forEach((prefix) => {
              const viewSlug = `${prefix}.view`;
              const viewExists = allPermissions.some((p) => p.slug === viewSlug);
              const viewIsVisible = visibleGroupSlugs.includes(viewSlug);
              if (viewExists && !prev.includes(viewSlug) && !viewIsVisible) {
                viewSlugsToAdd.add(viewSlug);
              }
            });
          } else {
            // Grupo normal: solo un .view
            const viewSlug = groupSlugs.find((slug) => slug.endsWith('.view'));
            const viewIsVisible = viewSlug && visibleGroupSlugs.includes(viewSlug);
            if (viewSlug && !prev.includes(viewSlug) && !viewIsVisible) {
              viewSlugsToAdd.add(viewSlug);
            }
          }

          // Agregar todos los permisos visibles que no estén seleccionados
          visibleGroupSlugs.forEach((slug) => {
            if (!prev.includes(slug)) {
              newSlugs.push(slug);
            }
          });

          return [...prev, ...newSlugs, ...Array.from(viewSlugsToAdd)];
        });
      }
    },
    [allPermissions, selectedPermissions, setSelectedPermissions, customGroupMap]
  );

  /**
   * Verifica si todos los permisos de un grupo están seleccionados
   */
  const isGroupFullySelected = useCallback(
    (groupPermissions: RolePermission[]) => {
      if (groupPermissions.length === 0) return false;
      // Verificar TODOS los permisos del grupo, incluyendo .view
      return groupPermissions.every((perm) => selectedPermissions.includes(perm.slug));
    },
    [selectedPermissions]
  );

  /**
   * Verifica si algunos permisos de un grupo están seleccionados (estado indeterminado)
   */
  const isGroupPartiallySelected = useCallback(
    (groupPermissions: RolePermission[]) => {
      if (groupPermissions.length === 0) return false;
      // Contar TODOS los permisos del grupo, incluyendo .view
      const selectedCount = groupPermissions.filter((perm) =>
        selectedPermissions.includes(perm.slug)
      ).length;
      return selectedCount > 0 && selectedCount < groupPermissions.length;
    },
    [selectedPermissions]
  );

  return {
    handlePermissionChange,
    handleSelectAll,
    handleGroupSelectAll,
    isGroupFullySelected,
    isGroupPartiallySelected,
  };
};

